import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { User, AlertRule, Alert } from '@moderepo/mode-apis';
import { fetchDeviceConfigSchemas, fetchEntities, fetchEntityById, fetchEntityClasses, fetchHomeById, fetchHomeMembers, fetchProjectHomes,
    selectDeviceConfigSchemas, selectEntities, selectEntityById, selectEntityClasses, selectHomeById, selectHomeMembers, selectProjectHomes,
    UIFetchEntityFilters, userAppDataContext, fetchAlertRules, selectAlertRules, selectAlertRuleById, fetchAlertRuleById, UIFetchAlertRulesFilters,
    UIFetchAlertsFilters, selectAlertById, fetchAlerts, selectAlerts, fetchAlertById, selectAllDeviceKVStores, fetchAllDeviceKVStores,
    selectAllHomeKVStores, fetchAllHomeKVStores } from '@moderepo/mode-data-state-user-app';
import { fetchProjectDeviceById, fetchUserById, ritzDataContext, selectLoggedInAgent, selectProjectById, selectProjectDeviceById,
    selectProjectUserById, selectSudoProjectAccessKey, selectMetricsReportSubscriptions, UIFetchMetricsReportSubscriptionFilters,
    fetchMetricsReportSubscriptions, selectOrganizationById, fetchOrganizationById, selectDeviceClasses, fetchDeviceClasses,
    fetchProjectFirmwareChannels, fetchProjectFirmwareBundles, selectFirmwareChannels, selectFirmwareBundles } from '@moderepo/mode-data-state-ritz';
import { Agent, Entity, EntityClass, Home, HomeMember, Project, ProjectDevice, MetricsReportSubscription, PaginationDataSet,
    DeviceConfigFirmwareSchema, OrganizationInfo, DeviceClass, FirmwareChannel, FirmwareBundle, KeyValuePair } from '@moderepo/mode-internal-apis';
import { useNavigate } from 'react-router-dom';
import { isEntitiesFeatureEnabled } from './utils';
import { RouteHelper } from '../containers';

// This file contains custom hooks for selecting/fetching data from data state. On each page or container, we usually select some
// data we need from the data state. And if the data is not fetched, we dispatch an action to fetch the data and when the data is ready,
// we make try to select the data again. We do this a lot therefore, we will create custom hooks to handle these select/fetch data so
// that we can do everything with just 1 line



/**
 * A custom hook to select a project by ID from the data state and fetch user data if data is not loaded.
 * @param projectId
 * @returns
 */
export const useSelectProject = (
    projectId: number | undefined,
): Project | undefined => {
    const { state } = useContext(ritzDataContext);
    const resultData = projectId ? selectProjectById(state, projectId) : undefined;

    return resultData;
};



/**
 * A custom hook to select a user by ID from the data state and fetch user data if data is not loaded.
 * @param projectId
 * @param userId
 * @param fetchLatest - If 'fetchLatest' is true then we will always fetch the latest data each time even if the data is already in cache.
 *                      This is for cases where we know the data change frequently and we MUST have the latest data.
 * @returns
 */
export const useSelectUser = (
    projectId: number | undefined, userId: number | undefined, fetchLatest?: boolean | undefined,
    showLoading?: boolean | undefined, suppressErrors?: boolean | undefined,
): User | undefined => {
    const { state, dispatch } = useContext(ritzDataContext);
    const resultData = userId ? selectProjectUserById(state, userId) : undefined;

    /**
     * This useEffect will take care of fetching data at least ONCE if 'fetchLatest' is true doesn't matter if we already have data in cache
     */
    useEffect(() => {
        if (userId && projectId && fetchLatest) {
            dispatch(fetchUserById(projectId, userId), showLoading, suppressErrors).catch(() => {
                // ignore error because the 'dispatch' function already handled showing the error
            });
        }
    }, [dispatch, fetchLatest, projectId, showLoading, suppressErrors, userId]);


    /**
     * This useEffect will only fetch data if we don't have data in cache yet
     */
    useEffect(() => {
        if (userId && projectId && !resultData) {
            dispatch(fetchUserById(projectId, userId), showLoading, suppressErrors).catch(() => {
                // ignore error because the 'dispatch' function already handled showing the error
            });
        }
    }, [dispatch, projectId, resultData, showLoading, suppressErrors, userId]);

    return resultData;
};



/**
 * A custom hook to select a home by ID from the data state and fetch home data if data is not loaded.
 * @param projectId
 * @param homeId
 * @param fetchLatest - If 'fetchLatest' is true then we will always fetch the latest data each time even if the data is already in cache.
 *                      This is for cases where we know the data change frequently and we MUST have the latest data.
 * @returns
 */
export const useSelectHome = (
    homeId: number | undefined, fetchLatest?: boolean | undefined, showLoading?: boolean | undefined, suppressErrors?: boolean | undefined,
): Home | undefined => {
    const { state, dispatch } = useContext(userAppDataContext);
    const resultData = homeId ? selectHomeById(state, homeId) : undefined;

    /**
     * This useEffect will take care of fetching data at least ONCE if 'fetchLatest' is true doesn't matter if we already have data in cache
     */
    useEffect(() => {
        if (homeId && fetchLatest) {
            dispatch(fetchHomeById(homeId), showLoading, suppressErrors).catch(() => {
                // ignore error because the 'dispatch' function already handled showing the error
            });

        }
    }, [dispatch, fetchLatest, homeId, showLoading, suppressErrors]);


    /**
     * This useEffect will only fetch data if we don't have data in cache yet
     */
    useEffect(() => {
        if (homeId && !resultData) {
            dispatch(fetchHomeById(homeId), showLoading, suppressErrors).catch(() => {
                // ignore error because the 'dispatch' function already handled showing the error
            });
        }
    }, [dispatch, resultData, homeId, showLoading, suppressErrors]);

    return resultData;
};


/**
 * A custom hook to select a home by ID from the data state and fetch home data if data is not loaded.
 * @param projectId
 * @param homeId
 * @param fetchLatest - If 'fetchLatest' is true then we will always fetch the latest data each time even if the data is already in cache.
 *                      This is for cases where we know the data change frequently and we MUST have the latest data.
 * @returns
 */
export const useSelectHomeMembers = (
    homeId: number | undefined, fetchLatest?: boolean | undefined, showLoading?: boolean | undefined, suppressErrors?: boolean | undefined,
): readonly HomeMember[] | undefined => {
    const { state, dispatch } = useContext(userAppDataContext);
    const resultData = homeId ? selectHomeMembers(state, homeId) : undefined;

    /**
     * This useEffect will take care of fetching data at least ONCE if 'fetchLatest' is true doesn't matter if we already have data in cache
     */
    useEffect(() => {
        if (homeId && fetchLatest) {
            dispatch(fetchHomeMembers(homeId), showLoading, suppressErrors).catch(() => {
                // ignore error because the 'dispatch' function already handled showing the error
            });
        }
    }, [dispatch, fetchLatest, homeId, showLoading, suppressErrors]);


    /**
     * This useEffect will only fetch data if we don't have data in cache yet
     */
    useEffect(() => {
        if (homeId && !resultData) {
            dispatch(fetchHomeMembers(homeId), showLoading, suppressErrors).catch(() => {
                // ignore error because the 'dispatch' function already handled showing the error
            });
        }
    }, [dispatch, resultData, homeId, showLoading, suppressErrors]);

    return resultData;
};


/**
 * A custom hook to select all home's KVs from the data state and fetch home KV if data is not loaded.
 * @param homeId
 * @param keyPrefix
 * @param fetchLatest - If 'fetchLatest' is true then we will always fetch the latest data each time even if the data is already in cache.
 *                      This is for cases where we know the data change frequently and we MUST have the latest data.
 * @returns
 */
export const useSelectAllHomeKVStores = (
    homeId: number | undefined, keyPrefix?: string | undefined, fetchLatest?: boolean | undefined,
    showLoading?: boolean | undefined, suppressErrors?: boolean | undefined,
): readonly KeyValuePair[] | undefined => {
    const { state, dispatch } = useContext(userAppDataContext);
    const resultData = homeId ? selectAllHomeKVStores(state, homeId, { keyPrefix }) : undefined;

    /**
     * This useEffect will take care of fetching data at least ONCE if 'fetchLatest' is true doesn't matter if we already have data in cache
     */
    useEffect(() => {
        if (homeId && fetchLatest) {
            dispatch(fetchAllHomeKVStores(homeId, keyPrefix), showLoading, suppressErrors).catch(() => {
                // ignore error because the 'dispatch' function already handled showing the error
            });

        }
    }, [dispatch, fetchLatest, homeId, showLoading, suppressErrors, keyPrefix]);


    /**
     * This useEffect will only fetch data if we don't have data in cache yet
     */
    useEffect(() => {
        if (homeId && !resultData) {
            dispatch(fetchAllHomeKVStores(homeId, keyPrefix), showLoading, suppressErrors).catch(() => {
                // ignore error because the 'dispatch' function already handled showing the error
            });
        }
    }, [dispatch, resultData, homeId, showLoading, suppressErrors, keyPrefix]);

    return resultData;
};



/**
 * Custom HOOK to get all the Project's homes from the data state. We will need this list of Project's homes so that we can create the Options
 * for the Home ID input field for the user to choose a home.
 * NOTE: the GET project homes API does not return ALL the homes because the API uses pagination. Not only that, we don't know how many home
 * there are total so we don't know how many request to make. Therefore, we need to TRY to get the homes for each page 1 at a time. If we use N
 * as page size and we get N homes back, we will assume there are more home to fetch and we make another API call to fetch the next page. If
 * we get less than N homes then we will assume we are at the end and stop fetching.
 */
export const useGetAllProjectHomes = (
    projectId: number, showLoading?: boolean | undefined, suppressErrors?: boolean | undefined,
): readonly Home[] | undefined => {

    // The pagination page size we will use to fetch data.
    const pageSize = 100;

    const { state:dataState } = useContext(ritzDataContext);
    const { state:appDataState, dispatch:appDataDispatch } = useContext(userAppDataContext);
    const [currentPage, setCurrentPage] = useState<number>(0);


    // Select all the homes we have fetched from the data state.
    const allHomes = useMemo(() => {
        // First, get the homes we got for the last page we requested
        const lastPageHomes = selectProjectHomes(appDataState, projectId, { pageNumber: currentPage, pageSize });

        // If we get the same number homes as the number we requested then that mean there are more homes to fetch therefore we will
        // increment the currentPage to trigger the useEffect to fetch data for the next page
        if (lastPageHomes && lastPageHomes.items.length >= pageSize) {
            setCurrentPage((pageNumber) => {
                return pageNumber + 1;
            });
        } else if (lastPageHomes) {
            // If we don't get any data back for the last fetch OR if the last fetch return LESS than requested then we will assume we are
            // at the last page so we can stop requesting data and can now get all the data from the data state
            const result: Home[] = [];
            for (let i = 0; i <= currentPage; i += 1) {
                const homes = selectProjectHomes(appDataState, projectId, { pageNumber: i, pageSize });
                if (homes) {
                    homes.items.forEach((home) => {
                        result.push(home);
                    });
                }
            }

            return result.sort((home1, home2) => {
                return home1.id - home2.id;
            });
        }

        return undefined;
    }, [appDataState, currentPage, projectId]);


    /**
     * This useEffect is used for fetching homes for the currentPage. Whenever the currentPage changed, this will make another call to fetch
     * the homes for the new currentPage.
     */
    useEffect(() => {
        if (!allHomes) {
            appDataDispatch(fetchProjectHomes(
                projectId, currentPage, pageSize,
                selectSudoProjectAccessKey(dataState, projectId)?.token,
            ), showLoading, suppressErrors).catch(() => {
                // ignore error because dataDispatch already handled displaying the error message
            });
        }
    }, [allHomes, appDataDispatch, currentPage, dataState, projectId, showLoading, suppressErrors]);

    return allHomes;
};



/**
 * A custom hook to select a project's device classes from the data state and fetch device classes if data is not loaded.
 * @param projectId
 * @param fetchLatest - If 'fetchLatest' is true then we will always fetch the latest data each time even if the data is already in cache.
 *                      This is for cases where we know the data change frequently and we MUST have the latest data.
 * @returns
 */
export const useSelectProjectDeviceClasses = (
    projectId: number | undefined, fetchLatest?: boolean | undefined,
    showLoading?: boolean | undefined, suppressErrors?: boolean | undefined,
): readonly DeviceClass[] | undefined => {
    const { state, dispatch } = useContext(ritzDataContext);
    const resultData = projectId ? selectDeviceClasses(state, projectId) : undefined;

    /**
     * This useEffect will take care of fetching data at least ONCE if 'fetchLatest' is true doesn't matter if we already have data in cache
     */
    useEffect(() => {
        if (projectId && fetchLatest) {
            dispatch(fetchDeviceClasses(projectId), showLoading, suppressErrors).catch(() => {
                // ignore error because the 'dispatch' function already handled showing the error
            });

        }
    }, [dispatch, fetchLatest, projectId, showLoading, suppressErrors]);


    /**
     * This useEffect will only fetch data if we don't have data in cache yet
     */
    useEffect(() => {
        if (projectId && !resultData) {
            dispatch(fetchDeviceClasses(projectId), showLoading, suppressErrors).catch(() => {
                // ignore error because the 'dispatch' function already handled showing the error
            });
        }
    }, [dispatch, projectId, resultData, showLoading, suppressErrors]);

    return resultData;
};



/**
 * A custom hook to select a project's firmware channels from the data state and fetch firmware channels if data is not loaded.
 * @param projectId
 * @param deviceClass
 * @param fetchLatest - If 'fetchLatest' is true then we will always fetch the latest data each time even if the data is already in cache.
 *                      This is for cases where we know the data change frequently and we MUST have the latest data.
 * @returns
 */
export const useSelectProjectFirmwareChannels = (
    projectId: number | undefined, deviceClass?: string | undefined, fetchLatest?: boolean | undefined,
    showLoading?: boolean | undefined, suppressErrors?: boolean | undefined,
): readonly FirmwareChannel[] | undefined => {
    const { state, dispatch } = useContext(ritzDataContext);
    const resultData = projectId ? selectFirmwareChannels(state, projectId, deviceClass) : undefined;

    /**
     * This useEffect will take care of fetching data at least ONCE if 'fetchLatest' is true doesn't matter if we already have data in cache
     */
    useEffect(() => {
        if (projectId && fetchLatest) {
            dispatch(fetchProjectFirmwareChannels(projectId, deviceClass), showLoading, suppressErrors).catch(() => {
                // ignore error because the 'dispatch' function already handled showing the error
            });

        }
    }, [deviceClass, dispatch, fetchLatest, projectId, showLoading, suppressErrors]);


    /**
     * This useEffect will only fetch data if we don't have data in cache yet
     */
    useEffect(() => {
        if (projectId && !resultData) {
            dispatch(fetchProjectFirmwareChannels(projectId, deviceClass), showLoading, suppressErrors).catch(() => {
                // ignore error because the 'dispatch' function already handled showing the error
            });
        }
    }, [deviceClass, dispatch, projectId, resultData, showLoading, suppressErrors]);

    return resultData;
};



/**
 * A custom hook to select a project's firmware bundles from the data state and fetch firmware bundles if data is not loaded.
 * @param projectId
 * @param deviceClass
 * @param fetchLatest - If 'fetchLatest' is true then we will always fetch the latest data each time even if the data is already in cache.
 *                      This is for cases where we know the data change frequently and we MUST have the latest data.
 * @returns
 */
export const useSelectProjectFirmwareBundles = (
    projectId: number | undefined, deviceClass?: string | undefined, fetchLatest?: boolean | undefined,
    showLoading?: boolean | undefined, suppressErrors?: boolean | undefined,
): readonly FirmwareBundle[] | undefined => {
    const { state, dispatch } = useContext(ritzDataContext);
    const resultData = projectId ? selectFirmwareBundles(state, projectId, deviceClass) : undefined;

    /**
     * This useEffect will take care of fetching data at least ONCE if 'fetchLatest' is true doesn't matter if we already have data in cache
     */
    useEffect(() => {
        if (projectId && fetchLatest) {
            dispatch(fetchProjectFirmwareBundles(projectId, deviceClass), showLoading, suppressErrors).catch(() => {
                // ignore error because the 'dispatch' function already handled showing the error
            });

        }
    }, [deviceClass, dispatch, fetchLatest, projectId, showLoading, suppressErrors]);


    /**
     * This useEffect will only fetch data if we don't have data in cache yet
     */
    useEffect(() => {
        if (projectId && !resultData) {
            dispatch(fetchProjectFirmwareBundles(projectId, deviceClass), showLoading, suppressErrors).catch(() => {
                // ignore error because the 'dispatch' function already handled showing the error
            });
        }
    }, [deviceClass, dispatch, projectId, resultData, showLoading, suppressErrors]);

    return resultData;
};



/**
 * A custom hook to select a device by ID from the data state and fetch device data if data is not loaded.
 * @param projectId
 * @param deviceId
 * @param fetchLatest - If 'fetchLatest' is true then we will always fetch the latest data each time even if the data is already in cache.
 *                      This is for cases where we know the data change frequently and we MUST have the latest data.
 * @returns
 */
export const useSelectDevice = (
    projectId: number | undefined, deviceId: number | undefined, fetchLatest?: boolean | undefined,
    showLoading?: boolean | undefined, suppressErrors?: boolean | undefined,
): ProjectDevice | undefined => {
    const { state, dispatch } = useContext(ritzDataContext);
    const resultData = deviceId ? selectProjectDeviceById(state, deviceId) : undefined;

    /**
     * This useEffect will take care of fetching data at least ONCE if 'fetchLatest' is true doesn't matter if we already have data in cache
     */
    useEffect(() => {
        if (deviceId && projectId && fetchLatest) {
            dispatch(fetchProjectDeviceById(projectId, deviceId), showLoading, suppressErrors).catch(() => {
                // ignore error because the 'dispatch' function already handled showing the error
            });

        }
    }, [dispatch, fetchLatest, projectId, deviceId, showLoading, suppressErrors]);


    /**
     * This useEffect will only fetch data if we don't have data in cache yet
     */
    useEffect(() => {
        if (deviceId && projectId && !resultData) {
            dispatch(fetchProjectDeviceById(projectId, deviceId), showLoading, suppressErrors).catch(() => {
                // ignore error because the 'dispatch' function already handled showing the error
            });
        }
    }, [dispatch, projectId, resultData, deviceId, showLoading, suppressErrors]);

    return resultData;
};



/**
 * A custom hook to select all device's KVs from the data state and fetch device KV if data is not loaded.
 * @param deviceId
 * @param keyPrefix
 * @param fetchLatest - If 'fetchLatest' is true then we will always fetch the latest data each time even if the data is already in cache.
 *                      This is for cases where we know the data change frequently and we MUST have the latest data.
 * @returns
 */
export const useSelectAllDeviceKVStores = (
    deviceId: number | undefined, keyPrefix?: string | undefined, fetchLatest?: boolean | undefined,
    showLoading?: boolean | undefined, suppressErrors?: boolean | undefined,
): readonly KeyValuePair[] | undefined => {
    const { state, dispatch } = useContext(userAppDataContext);
    const resultData = deviceId ? selectAllDeviceKVStores(state, deviceId, { keyPrefix }) : undefined;

    /**
     * This useEffect will take care of fetching data at least ONCE if 'fetchLatest' is true doesn't matter if we already have data in cache
     */
    useEffect(() => {
        if (deviceId && fetchLatest) {
            dispatch(fetchAllDeviceKVStores(deviceId, keyPrefix), showLoading, suppressErrors).catch(() => {
                // ignore error because the 'dispatch' function already handled showing the error
            });

        }
    }, [dispatch, fetchLatest, deviceId, showLoading, suppressErrors, keyPrefix]);


    /**
     * This useEffect will only fetch data if we don't have data in cache yet
     */
    useEffect(() => {
        if (deviceId && !resultData) {
            dispatch(fetchAllDeviceKVStores(deviceId, keyPrefix), showLoading, suppressErrors).catch(() => {
                // ignore error because the 'dispatch' function already handled showing the error
            });
        }
    }, [dispatch, resultData, deviceId, showLoading, suppressErrors, keyPrefix]);

    return resultData;
};



/**
 * A custom hook to select a device's config schemas from the data state and make API call to fetch the data if it is not loaded.
 * @param deviceId
 * @param fetchLatest - If 'fetchLatest' is true then we will always fetch the latest data each time even if the data is already in cache.
 *                      This is for cases where we know the data change frequently and we MUST have the latest data.
 * @returns
 */
export const useSelectDeviceConfigSchemas = (
    deviceId: number | undefined, fetchLatest?: boolean | undefined,
    showLoading?: boolean | undefined, suppressErrors?: boolean | undefined,
): readonly DeviceConfigFirmwareSchema[] | undefined => {
    const { state, dispatch } = useContext(userAppDataContext);
    const resultData = deviceId ? selectDeviceConfigSchemas(state, deviceId) : undefined;

    /**
     * This useEffect will take care of fetching data at least ONCE if 'fetchLatest' is true doesn't matter if we already have data in cache
     */
    useEffect(() => {
        if (deviceId && fetchLatest) {
            dispatch(fetchDeviceConfigSchemas(deviceId), showLoading, suppressErrors).catch(() => {
                // ignore error because the 'dispatch' function already handled showing the error
            });

        }
    }, [dispatch, fetchLatest, deviceId, showLoading, suppressErrors]);


    /**
     * This useEffect will only fetch data if we don't have data in cache yet
     */
    useEffect(() => {
        if (deviceId && !resultData) {
            dispatch(fetchDeviceConfigSchemas(deviceId), showLoading, suppressErrors).catch(() => {
                // ignore error because the 'dispatch' function already handled showing the error
            });
        }
    }, [dispatch, resultData, deviceId, showLoading, suppressErrors]);

    return resultData;
};



/**
 * A custom hook to select an entity by ID from the data state and fetch entity data if data is not loaded.
 * @param projectId
 * @param entityId
 * @param fetchLatest - If 'fetchLatest' is true then we will always fetch the latest data each time even if the data is already in cache.
 *                      This is for cases where we know the data change frequently and we MUST have the latest data.
 * @returns
 */
export const useSelectEntity = (
    projectId: number | undefined, entityId: string | undefined, fetchLatest?: boolean | undefined,
    showLoading?: boolean | undefined, suppressErrors?: boolean | undefined,
): Entity | undefined => {
    const { state, dispatch } = useContext(userAppDataContext);
    const resultData = projectId && entityId ? selectEntityById(state, projectId, entityId) : undefined;

    /**
     * This useEffect will take care of fetching data at least ONCE if 'fetchLatest' is true doesn't matter if we already have data in cache
     */
    useEffect(() => {
        if (entityId && projectId && fetchLatest) {
            dispatch(fetchEntityById(projectId, entityId), showLoading, suppressErrors).catch(() => {
                // ignore error because the 'dispatch' function already handled showing the error
            });

        }
    }, [dispatch, fetchLatest, projectId, entityId, showLoading, suppressErrors]);


    /**
     * This useEffect will only fetch data if we don't have data in cache yet
     */
    useEffect(() => {
        if (entityId && projectId && !resultData) {
            dispatch(fetchEntityById(projectId, entityId), showLoading, suppressErrors).catch(() => {
                // ignore error because the 'dispatch' function already handled showing the error
            });
        }
    }, [dispatch, projectId, resultData, entityId, showLoading, suppressErrors]);

    return resultData;
};


/**
 * Custom HOOK to get children for an Entity. Unlike the "useSelectAllEntityChildren" hook, this hook returns 1 page of entity children at a time
 */
export const useSelectEntityChildren = (
    projectId: number | undefined,
    parentId: string | undefined,
    filters?: UIFetchEntityFilters | undefined,
    fetchLatest?: boolean | undefined,
    showLoading?: boolean | undefined,
    suppressErrors?: boolean | undefined,
): PaginationDataSet<Entity> | undefined => {
    const { state, dispatch } = useContext(userAppDataContext);
    const combinedFilters = useMemo(() => {
        return { ...filters, parentId };
    }, [filters, parentId]);
    const resultData = projectId ? selectEntities(state, projectId, combinedFilters) : undefined;

    /**
     * This useEffect will take care of fetching data at least ONCE if 'fetchLatest' is true doesn't matter if we already have data in cache
     */
    useEffect(() => {
        if (fetchLatest && projectId) {
            dispatch(fetchEntities(projectId, combinedFilters), showLoading, suppressErrors).catch(() => {
                // ignore error because the 'dispatch' function already handled showing the error
            });
        }
    }, [combinedFilters, dispatch, fetchLatest, filters, parentId, projectId, showLoading, suppressErrors]);


    /**
     * This useEffect will only fetch data if we don't have data in cache yet
     */
    useEffect(() => {
        if (!resultData && projectId) {
            dispatch(fetchEntities(projectId, combinedFilters), showLoading, suppressErrors).catch(() => {
                // ignore error because the 'dispatch' function already handled showing the error
            });
        }
    }, [combinedFilters, dispatch, filters, parentId, projectId, resultData, showLoading, suppressErrors]);

    return resultData;
};



/**
 * Custom HOOK to get all children for an Entity
 * NOTE: the GET entity children does not return all children, it return only the number of children based on some specify limit therefore
 * to get all children, we need to make multiple get children call and combine them
 */
export const useSelectAllEntityChildren = (
    projectId: number | undefined, parentId: string | undefined,
    filters?: Omit<UIFetchEntityFilters, 'pageNumber' | 'pageSize'> | undefined,
    showLoading?: boolean | undefined, suppressErrors?: boolean | undefined,
): readonly Entity[] | undefined => {

    // The pagination page size we will use to fetch data.
    const pageSize = 100;

    const { state:dataState } = useContext(ritzDataContext);
    const { state:appDataState, dispatch:appDataDispatch } = useContext(userAppDataContext);
    const [currentPage, setCurrentPage] = useState<number>(0);

    const previousResult = useRef<readonly Entity[] | undefined>();

    // Select all the entities we have fetched from the data state.
    const allChildren = useMemo(() => {
        if (projectId === undefined) {
            return undefined;
        }

        // First, get the children we got for the last page we requested
        const lastPageChildren = selectEntities(appDataState, projectId, { ...filters, pageNumber: currentPage, pageSize, parentId });

        // If we get the same number entities as the number we requested then that mean there are more entities to fetch therefore we will
        // increment the currentPage to trigger the useEffect to fetch data for the next page
        if (lastPageChildren && lastPageChildren.items.length >= pageSize) {
            setCurrentPage((pageNumber) => {
                return pageNumber + 1;
            });
        } else if (lastPageChildren) {
            // If we don't get any data back for the last fetch OR if the last fetch return LESS than requested then we will assume we are
            // at the last page so we can stop requesting data and can now get all the data from the data state
            const result: Entity[] = [];
            for (let i = 0; i <= currentPage; i += 1) {
                const entities = selectEntities(appDataState, projectId, { ...filters, pageNumber: i, pageSize, parentId });
                if (entities) {
                    entities.items.forEach((entity) => {
                        result.push(entity);
                    });
                }
            }

            // We create a new array for 'result' each time this function is call and we return this new array back to the caller.
            // This can cause multiple unnecessary render call because this object will change every time. To prevent this, we will
            // compare the new result's elements to the previous result's elements to make sure it is really changed before we return
            // the new result. If the elements in the new result is exactly the same as the previous result then don't return the new result.
            const differentResults = !previousResult.current || previousResult.current.length !== result.length || result.find((entity, index) => {
                // find an entity from new result that is different from previousResult
                return entity !== previousResult.current?.[index];
            }) !== undefined;

            // If new result is NOT different than the previous result then return the previousResult
            if (!differentResults) {
                return previousResult.current;
            }

            // New result is different than the previous result so make the new result as previous result and return the new result
            previousResult.current = result;
            return result;
        }

        return undefined;
    }, [appDataState, currentPage, filters, parentId, projectId]);


    /**
     * This useEffect is used for fetching entities for the currentPage. Whenever the currentPage changed, this will make another call to fetch
     * the entities for the new currentPage.
     */
    useEffect(() => {
        if (projectId !== undefined && !allChildren) {
            appDataDispatch(fetchEntities(
                projectId, { ...filters, pageNumber: currentPage, pageSize, parentId },
            ), showLoading, suppressErrors).catch(() => {
                // ignore error because dataDispatch already handled displaying the error message
            });
        }
    }, [allChildren, appDataDispatch, currentPage, dataState, filters, parentId, projectId, showLoading, suppressErrors]);

    return allChildren;
};



/**
 * A custom hook to select all entity classes from the data state and fetch entity classes data if data is not loaded.
 * @param projectId
 * @param fetchLatest - If 'fetchLatest' is true then we will always fetch the latest data each time even if the data is already in cache.
 *                      This is for cases where we know the data change frequently and we MUST have the latest data.
 * @returns
 */
export const useSelectEntityClasses = (
    projectId: number | undefined, fetchLatest?: boolean | undefined, showLoading?: boolean | undefined, suppressErrors?: boolean | undefined,
): readonly EntityClass[] | undefined => {
    const { state:ritzDataState } = useContext(ritzDataContext);
    const { state, dispatch } = useContext(userAppDataContext);
    const resultData = projectId ? selectEntityClasses(state, projectId) : undefined;

    // Get the projectUISettings to see if entities feature is enabled before calling GET entity classes API.
    const project = projectId ? selectProjectById(ritzDataState, projectId) : undefined;
    const loggedInAgent = selectLoggedInAgent(ritzDataState) as Agent;
    const entityFeatureEnabled = isEntitiesFeatureEnabled(project, loggedInAgent);


    /**
     * This useEffect will take care of fetching data at least ONCE if 'fetchLatest' is true doesn't matter if we already have data in cache
     */
    useEffect(() => {
        if (entityFeatureEnabled && projectId && fetchLatest) {
            dispatch(fetchEntityClasses(projectId), showLoading, suppressErrors).catch(() => {
                // ignore error because the 'dispatch' function already handled showing the error
            });
        }
    }, [dispatch, entityFeatureEnabled, fetchLatest, loggedInAgent, project, projectId, showLoading, suppressErrors]);


    /**
     * This useEffect will only fetch data if we don't have data in cache yet
     */
    useEffect(() => {
        if (entityFeatureEnabled && projectId && !resultData) {
            dispatch(fetchEntityClasses(projectId), showLoading, suppressErrors).catch(() => {
                // ignore error because the 'dispatch' function already handled showing the error
            });
        }
    }, [dispatch, entityFeatureEnabled, loggedInAgent, project, projectId, resultData, showLoading, suppressErrors]);

    return resultData;
};



/**
 * A custom hook to select MetricsReportSubscriptions by some filters
 * @param fetchLatest - If 'fetchLatest' is true then we will always fetch the latest data each time even if the data is already in cache.
 *                      This is for cases where we know the data change frequently and we MUST have the latest data.
 * @returns
 */
export const useSelectMetricsReportSubscriptions = (
    filters: UIFetchMetricsReportSubscriptionFilters, fetchLatest?: boolean | undefined,
    showLoading?: boolean | undefined, suppressErrors?: boolean | undefined,
): PaginationDataSet<MetricsReportSubscription> | undefined => {
    const { state, dispatch } = useContext(ritzDataContext);
    const resultData = selectMetricsReportSubscriptions(state, filters);

    /**
     * This useEffect will take care of fetching data at least ONCE if 'fetchLatest' is true doesn't matter if we already have data in cache
     */
    useEffect(() => {
        if (fetchLatest) {
            dispatch(fetchMetricsReportSubscriptions(filters), showLoading, suppressErrors).catch(() => {
                // ignore error because the 'dispatch' function already handled showing the error
            });
        }
    }, [dispatch, fetchLatest, filters, showLoading, suppressErrors]);


    /**
     * This useEffect will only fetch data if we don't have data in cache yet
     */
    useEffect(() => {
        if (!resultData) {
            dispatch(fetchMetricsReportSubscriptions(filters), showLoading, suppressErrors).catch(() => {
                // ignore error because the 'dispatch' function already handled showing the error
            });
        }
    }, [dispatch, filters, resultData, showLoading, suppressErrors]);

    return resultData;
};



/**
 * A custom hook to select an organization by id from the data state and fetch organization data if data is not loaded.
 * @param orgId
 * @param fetchLatest - If 'fetchLatest' is true then we will always fetch the latest data each time even if the data is already in cache.
 *                      This is for cases where we know the data change frequently and we MUST have the latest data.
 * @returns
 */
export const useSelectOrganizationById = (
    orgId: string | undefined, fetchLatest?: boolean | undefined,
    showLoading?: boolean | undefined, suppressErrors?: boolean | undefined,
): OrganizationInfo | undefined => {
    const { state, dispatch } = useContext(ritzDataContext);
    const resultData = orgId ? selectOrganizationById(state, orgId) : undefined;
    const navigate = useNavigate();

    /**
     * This useEffect will take care of fetching data at least ONCE if 'fetchLatest' is true doesn't matter if we already have data in cache
     */
    useEffect(() => {
        if (orgId && fetchLatest) {
            dispatch(fetchOrganizationById(orgId), showLoading, suppressErrors).catch(() => {
                // take the user to the Not Authorized page
                RouteHelper.getInstance(navigate).gotoNotAuthorizedPage();
            });
        }
    }, [dispatch, fetchLatest, navigate, orgId, showLoading, suppressErrors]);


    /**
     * This useEffect will only fetch data if we don't have data in cache yet
     */
    useEffect(() => {
        if (orgId && !resultData) {
            dispatch(fetchOrganizationById(orgId), showLoading, suppressErrors).catch(() => {
                // take the user to the Not Authorized page
                RouteHelper.getInstance(navigate).gotoNotAuthorizedPage();
            });
        }
    }, [dispatch, resultData, orgId, showLoading, suppressErrors, navigate]);

    return resultData;
};



/**
 * A custom hook to select alert rules by some filters
 * @param fetchLatest - If 'fetchLatest' is true then we will always fetch the latest data each time even if the data is already in cache.
 *                      This is for cases where we know the data change frequently and we MUST have the latest data.
 * @returns
 */
export const useSelectAlertRules = (
    projectId: number | undefined,
    filters?: UIFetchAlertRulesFilters | undefined,
    fetchLatest?: boolean | undefined,
    showLoading?: boolean | undefined, suppressErrors?: boolean | undefined,
): PaginationDataSet<AlertRule> | undefined => {
    const { state, dispatch } = useContext(userAppDataContext);
    const resultData = projectId ? selectAlertRules(state, projectId, filters) : undefined;

    /**
     * This useEffect will take care of fetching data at least ONCE if 'fetchLatest' is true doesn't matter if we already have data in cache
     */
    useEffect(() => {
        if (fetchLatest && projectId) {
            dispatch(fetchAlertRules(projectId, filters), showLoading, suppressErrors).catch(() => {
                // ignore error because the 'dispatch' function already handled showing the error
            });
        }
    }, [dispatch, fetchLatest, filters, projectId, showLoading, suppressErrors]);


    /**
     * This useEffect will only fetch data if we don't have data in cache yet
     */
    useEffect(() => {
        if (!resultData && projectId) {
            dispatch(fetchAlertRules(projectId, filters), showLoading, suppressErrors).catch(() => {
                // ignore error because the 'dispatch' function already handled showing the error
            });
        }
    }, [dispatch, filters, projectId, resultData, showLoading, suppressErrors]);

    return resultData;
};


/**
 * A custom hook to select an alert rule by ID from the data state and fetch alert rule data if data is not loaded.
 * @param projectId
 * @param alertRuleId
 * @param fetchLatest - If 'fetchLatest' is true then we will always fetch the latest data each time even if the data is already in cache.
 *                      This is for cases where we know the data change frequently and we MUST have the latest data.
 * @returns
 */
export const useSelectAlertRule = (
    projectId: number | undefined, alertRuleId: number | undefined, fetchLatest?: boolean | undefined,
    showLoading?: boolean | undefined, suppressErrors?: boolean | undefined,
): AlertRule | undefined => {
    const { state, dispatch } = useContext(userAppDataContext);
    const resultData = projectId && alertRuleId ? selectAlertRuleById(state, projectId, alertRuleId) : undefined;

    /**
     * This useEffect will take care of fetching data at least ONCE if 'fetchLatest' is true doesn't matter if we already have data in cache
     */
    useEffect(() => {
        if (alertRuleId && projectId && fetchLatest) {
            dispatch(fetchAlertRuleById(projectId, alertRuleId), showLoading, suppressErrors).catch(() => {
                // ignore error because the 'dispatch' function already handled showing the error
            });

        }
    }, [dispatch, fetchLatest, projectId, alertRuleId, showLoading, suppressErrors]);


    /**
     * This useEffect will only fetch data if we don't have data in cache yet
     */
    useEffect(() => {
        if (alertRuleId && projectId && !resultData) {
            dispatch(fetchAlertRuleById(projectId, alertRuleId), showLoading, suppressErrors).catch(() => {
                // ignore error because the 'dispatch' function already handled showing the error
            });
        }
    }, [dispatch, projectId, resultData, alertRuleId, showLoading, suppressErrors]);

    return resultData;
};



/**
 * A custom hook to select alerts by some filters
 * @param fetchLatest - If 'fetchLatest' is true then we will always fetch the latest data each time even if the data is already in cache.
 *                      This is for cases where we know the data change frequently and we MUST have the latest data.
 * @returns
 */
export const useSelectAlerts = (
    projectId: number | undefined,
    filters?: UIFetchAlertsFilters | undefined,
    fetchLatest?: boolean | undefined,
    showLoading?: boolean | undefined, suppressErrors?: boolean | undefined,
): PaginationDataSet<Alert> | undefined => {
    const { state, dispatch } = useContext(userAppDataContext);
    const resultData = projectId ? selectAlerts(state, projectId, filters) : undefined;

    /**
     * This useEffect will take care of fetching data at least ONCE if 'fetchLatest' is true doesn't matter if we already have data in cache
     */
    useEffect(() => {
        if (fetchLatest && projectId) {
            dispatch(fetchAlerts(projectId, filters), showLoading, suppressErrors).catch(() => {
                // ignore error because the 'dispatch' function already handled showing the error
            });
        }
    }, [dispatch, fetchLatest, filters, projectId, showLoading, suppressErrors]);


    /**
     * This useEffect will only fetch data if we don't have data in cache yet
     */
    useEffect(() => {
        if (!resultData && projectId) {
            dispatch(fetchAlerts(projectId, filters), showLoading, suppressErrors).catch(() => {
                // ignore error because the 'dispatch' function already handled showing the error
            });
        }
    }, [dispatch, filters, projectId, resultData, showLoading, suppressErrors]);

    return resultData;
};


/**
 * A custom hook to select an alert by ID from the data state and fetch alert data if data is not loaded.
 * @param projectId
 * @param alertId
 * @param fetchLatest - If 'fetchLatest' is true then we will always fetch the latest data each time even if the data is already in cache.
 *                      This is for cases where we know the data change frequently and we MUST have the latest data.
 * @returns
 */
export const useSelectAlert = (
    projectId: number | undefined, alertId: string | undefined, fetchLatest?: boolean | undefined,
    showLoading?: boolean | undefined, suppressErrors?: boolean | undefined,
): Alert | undefined => {
    const { state, dispatch } = useContext(userAppDataContext);
    const resultData = projectId && alertId ? selectAlertById(state, projectId, alertId) : undefined;

    /**
     * This useEffect will take care of fetching data at least ONCE if 'fetchLatest' is true doesn't matter if we already have data in cache
     */
    useEffect(() => {
        if (alertId && projectId && fetchLatest) {
            dispatch(fetchAlertById(projectId, alertId), showLoading, suppressErrors).catch(() => {
                // ignore error because the 'dispatch' function already handled showing the error
            });
        }
    }, [dispatch, fetchLatest, projectId, alertId, showLoading, suppressErrors]);


    /**
     * This useEffect will only fetch data if we don't have data in cache yet
     */
    useEffect(() => {
        if (alertId && projectId && !resultData) {
            dispatch(fetchAlertById(projectId, alertId), showLoading, suppressErrors).catch(() => {
                // ignore error because the 'dispatch' function already handled showing the error
            });
        }
    }, [dispatch, projectId, resultData, alertId, showLoading, suppressErrors]);

    return resultData;
};
